import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, Renderer2, ViewChild, inject } from '@angular/core';
import { CalendarOptions, DatesSetArg, EventInput } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import multiMonthPlugin from '@fullcalendar/multimonth'; 
import { addDays, addMonths, endOfMonth, format, isSameMonth, isSameYear, parseISO, startOfMonth} from 'date-fns';
import interactionPlugin from '@fullcalendar/interaction';
import * as calendarModel from '@shared/classes/calendar.model';
import { Router, ActivatedRoute } from '@angular/router';
import { SharedService } from '@core/services/shared.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { MultiDateCalendarComponentComponent } from '@shared/features/modals/multi-date-calendar-component/multi-date-calendar-component.component';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AppService } from '@core/services/app.service';
import { FullCalendarComponent, FullCalendarModule } from '@fullcalendar/angular';
import { SwiperService } from '@core/services/swiper.service';
import { customEcommerce } from 'src/assets/custom-ecommerce/custom';


@Component({
    selector: 'app-calendar-view',
    templateUrl: './calendar-view.component.html',
    styleUrls: ['./calendar-view.component.scss'],
    imports: [FullCalendarModule]
})
export class CalendarViewComponent implements OnInit,AfterViewInit,OnDestroy {
  private renderer = inject(Renderer2);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private sharedService = inject(SharedService);
  private modalService = inject(BsModalService);
  private translate = inject(TranslateService);
  private appService = inject(AppService);
  private swiperService = inject(SwiperService);


  @ViewChild('calendarWrapper', { static: false }) calendarWrapper!: ElementRef;
  @ViewChild('calendar') calendarComponent!: FullCalendarComponent;
  readonly isMobileScreenSize: boolean = window.innerWidth < 550;
  @Input('calendarEvent') calendarEvent : calendarModel.CalendarProduct 
  @Input('isLoading') isLoading : calendarModel.CalendarProduct 
  swiperSubscription : Subscription

  isCustom : boolean;
  subdomain : string
  events : EventInput [] = [];
  rendered : boolean = false
  showPriceForCalendarCell : boolean = true
  calendarCellColor : string
  languageListener : Subscription
 
  calendarOptions: CalendarOptions = {
    initialView: 'multiMonthYear',
    initialDate: format(new Date(), 'yyyy-MM-dd'),
    plugins: [dayGridPlugin, multiMonthPlugin,interactionPlugin],
    height: 'auto',
    locale : 'en',
    firstDay: 1,
    fixedWeekCount: false,
    weekNumbers: false,
    headerToolbar: {
      left: 'prev',
      right: 'next',
    },
    contentHeight: 'auto',    
    views: {
      multiMonthYear: {
        type: 'multiMonth',
        duration: { months: this.isMobileScreenSize ? 1 : 2 },
        multiMonthMaxColumns: 2
      }
    },
    datesSet: () => {
     this.setBackgroundColor();
    }
  };

  
  ngOnInit() {
    this.isCustom = this.sharedService.checkRoute();
    if(this.isCustom){
      this.showPriceForCalendarCell = !customEcommerce[this.sharedService.getCustomDomain()].avoidShowingPriceinCalendarPage
      this.calendarCellColor = customEcommerce[this.sharedService.getCustomDomain()].calendarCellColor
    }
    this.calendarOptions.locale = localStorage.getItem('language') || this.getDeviceLanguage();
    this.events = this.mapToCalendarEvents(this.calendarEvent)
    this.languageListener = this.appService.languageObserver.subscribe((newLanguage) => {
      this.calendarOptions.locale = newLanguage;
      this.events = this.mapToCalendarEvents(this.calendarEvent)
    })
    
    this.calendarOptions.initialDate = this.calendarEvent.firstAvailableDate
    const firstAvailableDate = new Date(this.calendarEvent.firstAvailableDate);
    const lastAvailableDate = addDays(new Date(this.calendarEvent.lastAvailableDate), 1);
    let monthsToAdd = 0;

    let difference = (lastAvailableDate.getMonth() - firstAvailableDate.getMonth());
    if (difference % 2 === 0) {
      monthsToAdd = 1;
      difference = 0;
    } else {
      difference = 1;
    }

    const bufferedEndDate = addMonths(lastAvailableDate, monthsToAdd);

    this.calendarOptions.validRange = {
      start: format(startOfMonth(firstAvailableDate), 'yyyy-MM-dd'),
      end: difference === 1
        ? format(endOfMonth(lastAvailableDate), 'yyyy-MM-dd')
        : format(endOfMonth(bufferedEndDate), 'yyyy-MM-dd')
    };
  }

  ngAfterViewInit(): void {
    
    this.setBackgroundColor();
    this.handleCellCLick();

    if(window.innerWidth < 600){
      const calendarElement = this.calendarComponent.getApi().el;
      this.swiperSubscription = this.swiperService.setupSwipeGestures(calendarElement).subscribe((direction) => {
        if (direction === 'left') {
          this.calendarComponent.getApi().next();

        } else if (direction === 'right') {
          this.calendarComponent.getApi().prev();          
        }
      });
    }
  }


  getDeviceLanguage(): string {
    const language = navigator.language.split('-')[0];
  
    return this.sharedService.languages.includes(language) ? language : 'en';
  }
  mapToCalendarEvents(product: calendarModel.CalendarProduct): EventInput[] {
    const groupedEvents: Record<string, { minPrice: number; event: EventInput; eventsList: any[] }> = {};
  
    product.eventDates.forEach(event => {
      const startDate = format(parseISO(event.startDateTime), 'yyyy-MM-dd');
      const backCol = this.calendarCellColor ? this.calendarCellColor :
                      event.priceRate === 'low' ? '#B5F0E5' :
                      event.priceRate === 'mid' ? '#BEEFFA' : 
                      event.priceRate === 'high' ? '#FFD0CE' : 
                      "#a7d2fc";
                      
                      const pricePrefix = this.calendarOptions.locale === 'de' ? 'Ab' : 'da';
                      let formattedPrice = event.minPrice.toString().includes('.')
                      ? `${pricePrefix} ${event.minPrice.toString().replace('.', ',')}€`
                      : `${pricePrefix} ${event.minPrice},00€`;
                      
      if (!groupedEvents[startDate]) {
        groupedEvents[startDate] = {
          minPrice: event.minPrice,
          event: {
            title: this.showPriceForCalendarCell ? formattedPrice : '',
            start: startDate,
            backCol,
            hasMultipleEvents: false,
            multipleEvents: [event]
          },
          eventsList: [event]
        };
      } else {
        groupedEvents[startDate].eventsList.push(event);
      
        if (event.minPrice < groupedEvents[startDate].minPrice) {
          groupedEvents[startDate].minPrice = event.minPrice;
          groupedEvents[startDate].event.title = formattedPrice;
          groupedEvents[startDate].event.backCol = backCol;
        }
  
        groupedEvents[startDate].event.multipleEvents = [...groupedEvents[startDate].eventsList];
      }
      groupedEvents[startDate].event.hasMultipleEvents = groupedEvents[startDate].eventsList.length > 1;
    });
  
    return Object.values(groupedEvents).map(entry => entry.event);
  }
  
  
  handleCellCLick(){
    const calendarEl = this.calendarWrapper.nativeElement;
    calendarEl.addEventListener('click', (event: any) => {
      let tdElement = event.target.closest('td');
      if (tdElement) {
        const dateValue = tdElement.getAttribute('data-date');
        if (dateValue) {
          let singleEventClick=
          this.events.find(event=>{
            let singleEventDate =event.start;
            return singleEventDate === dateValue;
          })
          if (singleEventClick === undefined) return;
          const mvParam = this.activatedRoute.snapshot.queryParams['mv'];
          !(singleEventClick.hasMultipleEvents) && this.router.navigate([`/evento/${singleEventClick.multipleEvents[0].eventId}`], mvParam ? { queryParams: { mv: mvParam } } : {});
          singleEventClick.hasMultipleEvents && this.multiDatePopUP(singleEventClick,this.calendarEvent);
        }
      }
    });
  }

  multiDatePopUP(evetTime,data) {
    let initialState = {
      evetTime : evetTime,
      data :data
    }
    this.modalService.show(MultiDateCalendarComponentComponent,{ initialState, class: "modal-lg modal-dialog-centered"} );      
  }

  applyDateBackgroundColor(date: string, color: string) {
    const elements = document.querySelectorAll(`.fc-day[data-date="${date}"]`);
    elements.forEach((el) => {
      this.renderer.setStyle(el, 'background-color', color);
    });
  }
  
  setBackgroundColor(){
    this.events.forEach(event => {
      if (event.start && event.backCol) {
         this.applyDateBackgroundColor(event.start.toString(), event.backCol);
      }
    });
  } 


  ngOnDestroy(): void {
    if(window.innerWidth < 600){
      this.swiperSubscription.unsubscribe();
    }
    
  }

}
